import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import { TrendingUp, Target, ArrowUpRight, Award } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';

interface FDIHeadlinePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

export const FDIHeadlinePanel: React.FC<FDIHeadlinePanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { headline, history } = fdiDetailData;

  const quarters = [
    { label: 'Q1', value: headline.q1 },
    { label: 'Q2', value: headline.q2 },
    { label: 'Q3', value: headline.q3 },
    { label: 'Q4', value: headline.q4, record: true },
  ];

  const maxQ = Math.max(headline.q1, headline.q2, headline.q3, headline.q4);

  // Expanded content with full-size metrics and charts
  const expandedContent = (
    <div className="flex flex-col h-full gap-8">
      {/* Hero Section */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main FDI Value */}
        <div className="col-span-2 bg-terminal-bg rounded-lg p-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm text-gray-400 uppercase tracking-wide mb-2">
                Foreign Direct Investment 2024
              </div>
              <div className="text-6xl font-bold text-data-green mb-4">
                ${headline.total2024}B
              </div>
              <div className="flex items-center gap-3">
                <TrendingUp size={24} className="text-data-green" />
                <span className="text-2xl text-data-green font-semibold">
                  +{headline.yoyChange}% YoY
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Target Progress */}
        <div className="bg-terminal-bg rounded-lg p-6 flex flex-col">
          <div className="flex items-center gap-2 text-gray-400 text-sm uppercase tracking-wide mb-4">
            <Target size={16} />
            <span>2025 Target</span>
          </div>
          <div className="text-4xl font-bold text-data-amber mb-2">
            ${headline.target2025}B
          </div>
          <div className="text-sm text-gray-500 mb-4">5.5x growth from 2020</div>

          {/* Progress bar */}
          <div className="mt-auto">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>Progress</span>
              <span>{((headline.total2024 / headline.target2025) * 100).toFixed(0)}%</span>
            </div>
            <div className="h-3 bg-terminal-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-data-amber to-data-green rounded-full transition-all duration-500"
                style={{ width: `${(headline.total2024 / headline.target2025) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Compact content for normal panel state - Bloomberg hero style
  const compactContent = (
    <div className="flex flex-col h-full p-3">
      {/* Hero - Giant FDI Number */}
      <div className="text-center py-5 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20 mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Award size={16} className="text-data-amber" />
          <span className="text-xs text-data-amber uppercase tracking-widest font-semibold">
            2024 Record Year
          </span>
        </div>
        <div className="text-5xl font-mono font-black text-white mb-2">
          <span className="text-3xl">$</span>
          {headline.total2024}
          <span className="text-3xl">B</span>
        </div>
        <div className="flex items-center justify-center gap-2">
          <ArrowUpRight size={18} className="text-data-green" />
          <span className="text-xl font-semibold text-data-green">+{headline.yoyChange}%</span>
          <span className="text-sm text-gray-500">YoY</span>
        </div>
      </div>

      {/* Quarterly Performance Bars */}
      <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30 mb-4">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">Quarterly 2024</div>
        <div className="grid grid-cols-4 gap-3">
          {quarters.map((q) => (
            <div key={q.label} className="text-center">
              <div className="h-16 flex items-end justify-center mb-2">
                <div
                  className={`w-full rounded-t ${q.record ? 'bg-data-amber' : 'bg-data-green/70'}`}
                  style={{ height: `${(q.value / maxQ) * 100}%` }}
                />
              </div>
              <div className={`text-lg font-mono font-bold ${q.record ? 'text-data-amber' : 'text-white'}`}>
                ${q.value}B
              </div>
              <div className="text-xs text-gray-500">{q.label}</div>
              {q.record && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-data-amber/20 text-data-amber font-semibold">
                  RECORD
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-2xl font-mono font-bold text-data-green">{headline.fdiToGDP}%</div>
          <div className="text-xs text-gray-500">FDI / GDP</div>
        </div>
        <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
          <div className="text-2xl font-mono font-bold text-data-amber">${headline.cumulative5yr}B</div>
          <div className="text-xs text-gray-500">5-Year Cumulative</div>
        </div>
      </div>

      {/* Target Progress */}
      <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Target size={14} className="text-data-amber" />
            <span className="text-xs text-gray-400">2025 Target</span>
          </div>
          <span className="text-lg font-mono font-bold text-data-amber">${headline.target2025}B</span>
        </div>
        <div className="h-3 bg-terminal-border rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-data-green to-data-amber rounded-full"
            style={{ width: `${(headline.total2024 / headline.target2025) * 100}%` }}
          />
        </div>
        <div className="text-right text-xs text-gray-500 mt-1">
          {((headline.total2024 / headline.target2025) * 100).toFixed(0)}% achieved
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  // When expanded, render directly without Panel wrapper
  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="FDI OVERVIEW"
      subtitle="2024 RECORD"
      status="live"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default FDIHeadlinePanel;
